import { useMemo } from 'react';

import type { ChartDatapoint } from '../../../core/types/chart-data.js';
import { makeAccessor } from '../../../core/utils/makeAccessor.js';
import {
  type FlameIndex,
  buildFlameIndex,
} from '../utils/build-flame-index.js';
import { collectSubtree } from '../utils/collect-subtree.js';

type UseFlameDataArgs<TData extends ChartDatapoint> = {
  data: readonly TData[];
  idAccessor: string;
  parentIdAccessor: string;
  /**
   * Currently isolated span id, or `undefined` when the full dataset is displayed.
   */
  isolatedId: unknown;
  /**
   * Gates the isolation branch. When `false`, `isolatedRoot` is always `null`
   * and `effectiveData` returns the full indexed dataset, letting the same
   * hook power both the isolate-enabled and isolate-disabled configurations
   * of a flame component.
   */
  isolationEnabled: boolean;
};

type UseFlameDataResult<TData extends ChartDatapoint> = FlameIndex<TData> & {
  /**
   * The indexed datum matching `isolatedId`, or `null` when isolation is off or the id is not found.
   */
  isolatedRoot: TData | null;
  /**
   * `[isolatedRoot, ...descendants]` when isolated; the full indexed dataset otherwise.
   */
  effectiveData: TData[];
  /**
   * Id of the sole root of the currently visible chart, or `undefined` when no
   * such unambiguous root exists (multiple orphan roots visible). Equals
   * `isolatedId` when isolated; otherwise the id of the single raw-data root
   * if there is exactly one. Consumers use this to gate no-op actions — e.g.
   * disabling "isolate" on a span that already occupies the entire chart.
   */
  visibleRootId: unknown;
};

/**
 * Shared data hook for FlameChart / FlameGraph. Wraps `buildFlameIndex`
 * (single `O(n)` pass over raw data) and `collectSubtree` (walk from the
 * isolation root) in memos keyed on the inputs that actually matter, so
 * the expensive index build only reruns when `data` or the id accessors
 * change — never on a re-render triggered by isolation navigation alone.
 * @internal
 */
export function useFlameData<TData extends ChartDatapoint>({
  data,
  idAccessor,
  parentIdAccessor,
  isolatedId,
  isolationEnabled,
}: UseFlameDataArgs<TData>): UseFlameDataResult<TData> {
  const readId = makeAccessor(idAccessor);
  const readParentId = makeAccessor(parentIdAccessor);

  const index = useMemo(
    () => buildFlameIndex(data, { idAccessor, parentIdAccessor }),
    [data, idAccessor, parentIdAccessor],
  );

  const isolatedRoot = useMemo(() => {
    if (!isolationEnabled || isolatedId === undefined) {
      return null;
    }
    return index.byId.get(isolatedId) ?? null;
  }, [index, isolatedId, isolationEnabled]);

  const effectiveData = useMemo(() => {
    if (isolatedRoot === null) {
      return index.indexedData;
    }
    return collectSubtree(
      isolatedRoot,
      readId(isolatedRoot),
      index.childrenByParentId,
      idAccessor,
    );
  }, [index, isolatedRoot, idAccessor]);

  const soleRootId = useMemo<unknown>(() => {
    let candidate: unknown = undefined;
    let count = 0;
    for (const span of index.indexedData) {
      const parentId = readParentId(span);
      const isRoot =
        parentId === null ||
        parentId === undefined ||
        !index.byId.has(parentId);
      if (!isRoot) {
        continue;
      }
      if (count === 0) {
        candidate = readId(span);
      }
      count++;
      if (count > 1) {
        return undefined;
      }
    }
    return count === 1 ? candidate : undefined;
  }, [index, idAccessor, parentIdAccessor]);

  const visibleRootId =
    isolatedRoot !== null ? readId(isolatedRoot) : soleRootId;

  return {
    indexedData: index.indexedData,
    byId: index.byId,
    childrenByParentId: index.childrenByParentId,
    isolatedRoot,
    effectiveData,
    visibleRootId,
  };
}
